import { SubmitHandler } from "react-hook-form";
import { useRefillCardForm } from "./useRefillCardForm";
import { IRefillCardFormModel } from "../models/refillCardFormModel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IRefillCardInitialDataModel } from "../models/refillCardInitialDataModel";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { useAppContext } from "../../../../../AppContext";
import { getDirectSalePaymentOptions } from "../../../../../common/utilities/paymentUtility";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { PageMode } from "../../../../../common/types";
import { PaymentTypes, PlanConsumerType } from "../../../../../common/enums";
import { dateUtility } from "../../../../../common/infrastructure/utilities/dateUtility";
import {
  constructCashdeskHref,
  constructFamilyHref,
  constructUserHref,
  constructUserPlanHref,
} from "../../../../../common/infrastructure/routes/routePaths";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { refillCardConfirmMessages } from "../helpers/refillCardConfirmMessages";

export const useRefillCardPage = (pageMode: PageMode) => {
  const { userPlanId = 0 } = useParams();
  const { selectedStudioId } = useAppContext();
  const navigate = useNavigate();
  const { mutate } = useApiMutation();

  // Store original values for comparison in edit mode
  const [originalPrice, setOriginalPrice] = useState<number | null>(null);
  const [originalNumberOfVisits, setOriginalNumberOfVisits] = useState<
    number | null
  >(null);
  const [originalRemainingVisits, setOriginalRemainingVisits] = useState<
    number | null
  >(null);

  const { data: initialData, isLoading: isInitialDataLoading } =
    useApiQuery<IRefillCardInitialDataModel>({
      queryKey: [
        endpointsMetadata.plan.wizardRefillCardInitialData,
        userPlanId,
        selectedStudioId,
      ],
      endpoint: `${endpointsMetadata.plan.wizardRefillCardInitialData}?userPlanId=${userPlanId}`,
      disableErrorToast: false,
      retry: false,
    });

  const { data: studioInstructors, isLoading: isStudioInstructorsLoading } =
    useApiQuery<ISelectFieldModel[]>({
      queryKey: [endpointsMetadata.studio.getStudioInstructors, userPlanId],
      endpoint: `${endpointsMetadata.studio.getStudioInstructors}?userPlanId=${userPlanId}`,
      enabled: selectedStudioId > 0,
    });

  const paymentOptions = useMemo(() => {
    return getDirectSalePaymentOptions(
      initialData?.studioSettings?.paymentTypes,
    );
  }, [initialData]);

  const isLoading = !!isInitialDataLoading || !!isStudioInstructorsLoading;

  const { currentlySelectedPlan, form, availableUsers, availableFamilies } =
    useRefillCardForm(
      isLoading,
      selectedStudioId,
      studioInstructors,
      paymentOptions.map((x) => x.id) as PaymentTypes[],
      initialData,
      Number(userPlanId)
    );

  const getEmptyMessage = () => {
    if (!isInitialDataLoading) {
      if (initialData?.plans?.length === 0) {
        return "Към момента нямате налични планове.";
      }
    }
    return undefined;
  };

  const emptyMessage = useMemo(() => {
    return getEmptyMessage();
  }, [isInitialDataLoading, getEmptyMessage]);

  const lastUserId = useRef<number>();
  const lastFamilyId = useRef<number>();
  const openCashdeskAfterRef = useRef(false);

  const {
    createBothChangedMessage,
    createPriceChangedMessage,
    createVisitsChangedMessage,
  } = refillCardConfirmMessages();

  // Store original values when editing
  useEffect(() => {
    if (
      pageMode === "edit" &&
      initialData?.currentlySelectedSubscription &&
      !isInitialDataLoading
    ) {
      const sub = initialData.currentlySelectedSubscription;
      setOriginalPrice(sub.price ?? null);
      setOriginalNumberOfVisits(sub.originalNumberOfVisits ?? null);
      setOriginalRemainingVisits(sub.numberOfVisits ?? null);
    }
  }, [pageMode, initialData, isInitialDataLoading]);

  const calculatePricePerVisit = (
    price: number | null,
    totalVisits: number | null
  ): number => {
    if (!price || !totalVisits || totalVisits === 0) return 0;
    return Math.round((price / totalVisits) * 100) / 100;
  };

  // Calculate price per visit dynamically for display
  const currentPrice = form.watch("price");
  const currentNumberOfVisits = form.watch("numberOfVisits");
  const currentOriginalNumberOfVisits = form.watch("originalNumberOfVisits");

  const pricePerVisit = useMemo(() => {
    // Convert form values to numbers to avoid string concatenation bugs
    const price = Number(currentPrice);
    const numberOfVisits = Number(currentNumberOfVisits);
    const originalTotal = Number(currentOriginalNumberOfVisits);

    if (!price || !numberOfVisits) return null;

    let totalVisits = numberOfVisits;

    // In edit mode, calculate total visits (used + remaining)
    if (pageMode === "edit" && originalTotal) {
      const originalRemaining =
        initialData?.currentlySelectedSubscription?.numberOfVisits ?? 0;
      const usedVisits = originalTotal - originalRemaining;
      totalVisits = usedVisits + numberOfVisits;
    }

    if (totalVisits === 0) return null;

    return (price / totalVisits).toFixed(2);
  }, [
    currentPrice,
    currentNumberOfVisits,
    pageMode,
    currentOriginalNumberOfVisits,
    initialData,
  ]);

  const performSave = async (data: IRefillCardFormModel) => {
    lastUserId.current = data.userId;
    lastFamilyId.current = data.familyId;

    if (data.startDate) {
      data.startDate = dateUtility.resetTime(data.startDate);
    }
    if (data.endDate) {
      data.endDate = dateUtility.resetTime(data.endDate);
    }

    mutate(
      {
        endpoint: endpointsMetadata.plan.refillCard,
        globalIsLoading: true,
        method: "post",
        invalidateKeys: [
          [endpointsMetadata.studio.getAll],
          [endpointsMetadata.initialization.getUserStudios],
          [endpointsMetadata.plan.wizardRefillCardInitialData],
        ],
        ...data,
      },
      {
        onSuccess: () => {
          if (openCashdeskAfterRef.current) {
            openCashdeskAfterRef.current = false;
            navigate(
              constructCashdeskHref({ userId: lastUserId.current }),
            );
          } else if (
            lastUserId.current &&
            currentlySelectedPlan?.consumerType === PlanConsumerType.User
          ) {
            navigate(constructUserHref(lastUserId.current));
          } else {
            navigate(constructFamilyHref(lastFamilyId.current ?? 0));
          }
        },
      }
    );
  };

  const onSave: SubmitHandler<IRefillCardFormModel> = async (data) => {
    // Only show confirms in edit mode
    if (
      pageMode !== "edit" ||
      !originalPrice ||
      !originalNumberOfVisits ||
      !originalRemainingVisits
    ) {
      await performSave(data);
      return;
    }

    const originalTotalVisits = originalNumberOfVisits;
    const usedVisits = originalTotalVisits - originalRemainingVisits;

    const newRemainingVisits = data.numberOfVisits;
    const newTotalVisits = usedVisits + newRemainingVisits;
    const newPrice = data.price ?? 0;

    const priceChanged = originalPrice !== newPrice;
    const visitsChanged = originalRemainingVisits !== newRemainingVisits;

    // If nothing changed, just save
    if (!priceChanged && !visitsChanged) {
      await performSave(data);
      return;
    }

    const originalPricePerVisit = calculatePricePerVisit(
      originalPrice,
      originalTotalVisits
    );
    const newPricePerVisit = calculatePricePerVisit(newPrice, newTotalVisits);

    // Both price and visits changed
    if (priceChanged && visitsChanged) {
      showCustomConfirmDialog(
        createBothChangedMessage(
          originalPrice,
          originalTotalVisits,
          originalPricePerVisit,
          newPrice,
          newTotalVisits,
          newPricePerVisit
        ),
        () => performSave(data),
        () => {},
        "warning",
        "Да",
        "Не"
      );
      return;
    }

    // Only price changed
    if (priceChanged) {
      showCustomConfirmDialog(
        createPriceChangedMessage(originalPricePerVisit, newPricePerVisit),
        () => performSave(data),
        () => {},
        "warning",
        "Да",
        "Не"
      );
      return;
    }

    // Only visits changed
    if (visitsChanged) {
      const adjustedPrice =
        Math.round(originalPricePerVisit * newTotalVisits * 100) / 100;

      showCustomConfirmDialog(
        createVisitsChangedMessage(
          originalPrice,
          originalTotalVisits,
          originalPricePerVisit,
          newTotalVisits,
          newPrice,
          newPricePerVisit,
          adjustedPrice
        ),
        () => performSave({ ...data, price: adjustedPrice }),
        () => performSave(data),
        "warning",
        "Да",
        "Не"
      );
      return;
    }
  };

  const removePlan = () => {
    if (initialData?.currentlySelectedSubscription.isDeleted) {
      initiatePlanToggle();
    } else {
      showCustomConfirmDialog(
        "Сигурни ли сте, че искате да изтриете този план? Планът ще бъде премахнат, но всички тренировки, които потребителя е заплатил с него, ще останат.",
        initiatePlanToggle,
        () => {},
        "warning"
      );
    }
  };

  const initiatePlanToggle = async () => {
    try {
      await mutate({
        endpoint: endpointsMetadata.plan.toggleSubscriptionDeletionStatus,
        globalIsLoading: true,
        method: "delete",
        queryParams: {
          subscriptionId: userPlanId,
        },
        invalidateKeys: [
          [endpointsMetadata.user.getUser],
          [endpointsMetadata.user.wizardGetClient],
          [endpointsMetadata.plan.wizardRefillCardInitialData],
          [endpointsMetadata.planner.getEventArrivalConfirmationInfo],
        ],
      });
    } catch {
    } finally {
      constructUserPlanHref(Number(userPlanId));
    }
  };

  const onSaveAndOpenCashdesk = useCallback(() => {
    openCashdeskAfterRef.current = true;
    form.handleSubmit(onSave)();
  }, [form, onSave]);

  return {
    form,
    availablePlans: initialData?.plans.map((x) => x.plan),
    paymentOptions,
    studioInstructors,
    availableUsers,
    availableFamilies,
    isEdit: pageMode === "edit",
    initialData,
    removePlan,
    isLoading,
    emptyMessage,
    currentlySelectedPlan,
    onSave,
    onSaveAndOpenCashdesk,
    pricePerVisit,
  };
};
